import React from "react";
import Report from "../report/report";
import {Utils} from "../../../helpers/utils";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {IPaginationStore} from "../../../common/stores/paginationStore";
import {storesContext} from "../../../common/stores/stores";
import i18n from "../../../i18n/i18n";
import DropDown from "../../../common/components/shared/dropDown/dropDown";
import {ValidatedValue} from "../../../helpers/validatedValue";
import {DropDownModel} from "../../../common/models/dropDown";
import {FerryDropDownModel} from "../../../dictionaries/models/ferry";
import {FerryService} from "../../../dictionaries/services/ferryService";
import DateTimePicker from "../../../common/components/shared/dateTimePicker/dateTimePicker";
import Validator from "../../../helpers/validator";
import {DateUtils} from "../../../helpers/dateUtils";
import {FerryHandlingTimeModel, FerryHandlingTimeFilters} from "../../models/ferryHandlingTime";
import {FerryHandlingTimeService} from "../../services/ferryHandlingTimeService";
import {DropDownUtils} from "../../../helpers/dropDownUtils";
import {TimetableActionTypes} from "../../../timetables/models/timetable";
import TextInput from "../../../common/components/shared/textInput/textInput";
import {Api} from "../../../helpers/api";
import Loader from "../../../common/components/shared/loader/loader";

interface FerryHandlingTimeState {
    selectedFerry: ValidatedValue<string>;
    selectedArrivalFerryFrom: ValidatedValue<Date | null>;
    selectedArrivalFerryTo: ValidatedValue<Date | null>;
    selectedDepartureFerryFrom: ValidatedValue<Date | null>;
    selectedDepartureFerryTo: ValidatedValue<Date | null>;
    selectedVoyageNo: ValidatedValue<string>;
    selectedActionType: ValidatedValue<TimetableActionTypes | "">;
    isSubmitButtonBlocked: boolean;
    isReportGenerated: boolean;
    generatedReport: FerryHandlingTimeModel[] | undefined | null;
    generatedReportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    ferries: FerryDropDownModel[];
    ferriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

export default class FerryHandlingTime extends React.Component <any, FerryHandlingTimeState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            selectedFerry: new ValidatedValue(""),
            selectedArrivalFerryFrom: new ValidatedValue(null),
            selectedArrivalFerryTo: new ValidatedValue(null),
            selectedDepartureFerryFrom: new ValidatedValue(null),
            selectedDepartureFerryTo: new ValidatedValue(null),
            selectedVoyageNo: new ValidatedValue(""),
            selectedActionType: new ValidatedValue(""),
            isSubmitButtonBlocked: false,
            isReportGenerated: false,
            generatedReport: undefined,
            generatedReportResponse: undefined,
            currentPage: 0,
            ferries: [],
            ferriesResponse: undefined
        };

        Validator.addOneDateRequiredValidator(this.state.selectedArrivalFerryFrom, this.state.selectedDepartureFerryFrom);
    }

    async componentDidMount() {
        await this.getFerries();
    }

    public render(): JSX.Element {
        const properties = ["ferryName", "actionType", "voyageNo",
            "firstEntryToTerminal", "lastEntryToTerminal", "entryTimeDifference",
            "scheduledArrivalTimeOfFerry", "actualArrivalTimeOfFerry", "differenceArrivalTime",
            "scheduledDepartureTimeOfFerry", "actualDepartureTimeOfFerry", "differenceDepartureTime",
            "notes"
        ];
        const headers = properties.map(key => i18n.t("reports.ferryHandlingTime.headers." + key));

        if (this.state.ferries.length > 0 || !Api.isSuccess(this.state.ferriesResponse)) {
            return (
                <Report
                    className="ferry-handling-time"
                    filters={this.renderFilters()}
                    onGenerateReport={this.onGenerateReport.bind(this)}
                    generatedReport={this.state.generatedReport}
                    generatedReportResponse={this.state.generatedReportResponse}
                    isReportGenerated={this.state.isReportGenerated}
                    reportTestId="ferry-handling-time"
                    reportProperties={properties}
                    reportHeaders={headers}
                    reportTableRenderCell={this.renderCell}
                    currentPage={this.state.currentPage}
                    reportService={FerryHandlingTimeService}
                    isSummaryReportGenerated={false}
                    selectedFilters={
                        {
                            arrivalFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryFrom.value),
                            arrivalFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryTo.value),
                            departureFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryFrom.value),
                            departureFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryTo.value),
                            ferryEntityId: this.state.selectedFerry.value,
                            voyageNo: this.state.selectedVoyageNo.value,
                            actionType: this.state.selectedActionType.value
                        } as FerryHandlingTimeFilters
                    }
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    summaryCurrentPage={0}
                    summaryNumberOfElements={0}
                />
            );
        } else {
            return <Loader/>;
        }
    }

    private renderFilters(): JSX.Element {
        return (
            <>
                <DateTimePicker
                    label={i18n.t("reports.ferryHandlingTime.filters.arrivalFerryFrom")}
                    validatedValue={this.state.selectedArrivalFerryFrom}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedArrivalFerryFrom.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedArrivalFerryFrom, this.state.selectedArrivalFerryTo);
                        Validator.addOneDateRequiredValidator(this.state.selectedArrivalFerryFrom, this.state.selectedDepartureFerryFrom);
                    }}
                    testId="filters-arrival-ferry-from"
                />
                <DateTimePicker
                    label={i18n.t("reports.ferryHandlingTime.filters.arrivalFerryTo")}
                    validatedValue={this.state.selectedArrivalFerryTo}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedArrivalFerryTo.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedArrivalFerryFrom, this.state.selectedArrivalFerryTo);
                        Validator.addOneDateRequiredValidator(this.state.selectedArrivalFerryFrom, this.state.selectedDepartureFerryFrom);
                    }}
                    testId="filters-arrival-ferry-to"
                />
                <DateTimePicker
                    label={i18n.t("reports.ferryHandlingTime.filters.departureFerryFrom")}
                    validatedValue={this.state.selectedDepartureFerryFrom}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDepartureFerryFrom.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDepartureFerryFrom, this.state.selectedDepartureFerryTo);
                        Validator.addOneDateRequiredValidator(this.state.selectedArrivalFerryFrom, this.state.selectedDepartureFerryFrom);
                    }}
                    testId="filters-departure-ferry-from"
                />
                <DateTimePicker
                    label={i18n.t("reports.ferryHandlingTime.filters.departureFerryTo")}
                    validatedValue={this.state.selectedDepartureFerryTo}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDepartureFerryTo.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDepartureFerryFrom, this.state.selectedDepartureFerryTo);
                        Validator.addOneDateRequiredValidator(this.state.selectedArrivalFerryFrom, this.state.selectedDepartureFerryFrom);
                    }}
                    testId="filters-departure-ferry-to"
                />
                <DropDown
                    label={i18n.t("reports.ferryHandlingTime.filters.ferry")}
                    items={this.ferriesToDropdown()}
                    validatedValue={this.state.selectedFerry}
                    testId="filters-ferry"
                    withEmptyValue
                    error={!Api.isSuccess(this.state.ferriesResponse) ? i18n.t("errorMessages.ferriesErrorSuffix") : ""}
                    reloadFunc={() => this.getFerries()}
                    isNoRequired
                />
                <TextInput
                    label={i18n.t("reports.ferryHandlingTime.filters.voyageNo")}
                    validatedValue={this.state.selectedVoyageNo}
                    testId="filters-voyage-no"
                />
                <DropDown
                    label={i18n.t("reports.ferryHandlingTime.filters.actionType")}
                    items={this.actionTypeToDropDownMap}
                    validatedValue={this.state.selectedActionType}
                    testId="filters-action-type"
                    withEmptyValue
                />
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "firstEntryToTerminal":
            case "lastEntryToTerminal":
            case "scheduledArrivalTimeOfFerry":
            case "actualArrivalTimeOfFerry":
            case "scheduledDepartureTimeOfFerry":
            case "actualDepartureTimeOfFerry":
                return <>{DateUtils.getFormattedDateWithTime((row[property]))}</>;
            case "actionType": {
                const actionType = row[property].toLowerCase();
                const translatedActionType = i18n.t(`actionType.${actionType}`);
                return <>{Utils.upperCaseFirstLetter(translatedActionType)}</>;
            }
            default:
                return row[property];
        }
    }

    private get ferryToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.ferries, "entityId");
    }

    private ferriesToDropdown() {
        return DropDownUtils.listToDropDownMap(this.state.ferries, "entityId");
    }

    private get actionTypeToDropDownMap(): DropDownModel[] {
        return [
            {
                id: TimetableActionTypes.ARRIVAL,
                name: Utils.upperCaseFirstLetter(i18n.t("actionType.arrival")),
            },
            {
                id: TimetableActionTypes.DEPARTURE,
                name: Utils.upperCaseFirstLetter(i18n.t("actionType.departure")),
            }
        ];
    }

    private get isValid(): boolean {
        this.state.selectedArrivalFerryFrom.validate();
        this.state.selectedArrivalFerryTo.validate();
        this.state.selectedDepartureFerryFrom.validate();
        this.state.selectedDepartureFerryTo.validate();
        this.state.selectedFerry.validate();
        this.state.selectedVoyageNo.validate();
        this.state.selectedActionType.validate();

        return this.state.selectedArrivalFerryFrom.isValid && this.state.selectedArrivalFerryTo.isValid
            && this.state.selectedDepartureFerryFrom.isValid && this.state.selectedDepartureFerryTo.isValid
            && this.state.selectedFerry.isValid && this.state.selectedVoyageNo.isValid && this.state.selectedActionType.isValid;
    }

    private async onGenerateReport() {
        this.setState({generatedReportResponse: undefined});
        const paginationStore = this.context.paginationStore as IPaginationStore;

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});

            const filters = {
                arrivalFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryFrom.value),
                arrivalFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryTo.value),
                departureFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryFrom.value),
                departureFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryTo.value),
                ferryEntityId: this.state.selectedFerry.value,
                voyageNo: this.state.selectedVoyageNo.value.trim(),
                actionType: this.state.selectedActionType.value
            };

            const generatedReportResponse = await FerryHandlingTimeService.generateReport(
                filters
            );

            this.setState({
                isSubmitButtonBlocked: false,
                generatedReportResponse,
                generatedReport: generatedReportResponse?.data?.data || null,
                isReportGenerated: true,
                currentPage: generatedReportResponse?.data?.currentPage
            });

            paginationStore.setNumberOfElements(generatedReportResponse?.data?.numberOfElements);
            paginationStore.setNumberOfPages(generatedReportResponse?.data?.numberOfPages);
        }

        Utils.scrollToFirstErrorMessage();
    }

    private async getFerries() {
        const ferriesResponse = await FerryService.getFerriesForDropDown();

        this.setState({
            ferries: (ferriesResponse)?.data || [],
            ferriesResponse,
        });
    }
}